import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, LogOut, FileText, Briefcase, Link2, Phone, MessageSquare, Users, ChevronDown, ChevronUp, Plus, Trash2, Upload, Image, Star, GripVertical, Eye, ShoppingBag, Clock, Truck, Store, Check, X as XIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

function Section({ title, icon: Icon, children, defaultOpen = false, count }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl overflow-hidden mb-4" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left text-white hover:bg-white/5 transition-colors">
        <span className="flex items-center gap-3 text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          <Icon size={20} style={{ color: '#F78A7D' }} /> {title}
          {count !== undefined && <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/50">{count}</span>}
        </span>
        {open ? <ChevronUp size={18} className="text-white/40" /> : <ChevronDown size={18} className="text-white/40" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-4 border-t" style={{ borderColor: '#2C3529' }}>{children}</div>}
    </div>
  );
}

function Field({ label, value, onChange, multiline = false, placeholder = '' }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1 text-white/50">{label}</label>
      {multiline ? (
        <textarea rows={4} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none resize-none focus:ring-1 focus:ring-[#F78A7D]/50"
          style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
      ) : (
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-1 focus:ring-[#F78A7D]/50"
          style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
      )}
    </div>
  );
}

function ImageUpload({ currentUrl, onUpload, label = "Image" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload`, fd, { ...auth(), headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      onUpload(`${API}${data.url}`);
    } catch (err) {
      alert('Erreur de telechargement');
    }
    setUploading(false);
  };

  return (
    <div>
      <label className="block text-xs font-medium mb-1 text-white/50">{label}</label>
      <div className="flex items-start gap-3">
        {currentUrl && (
          <img src={currentUrl} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" style={{ border: '1px solid #2C3529' }} />
        )}
        <div className="flex-1 space-y-2">
          <input type="text" value={currentUrl || ''} onChange={e => onUpload(e.target.value)} placeholder="URL de l'image ou televersez"
            className="w-full px-3 py-2 rounded-lg text-xs text-white outline-none"
            style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
            style={{ border: '1px dashed #2C3529' }}>
            <Upload size={12} /> {uploading ? 'Telechargement...' : 'Televerser une image'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
        </div>
      </div>
    </div>
  );
}

function SaveBtn({ onClick, saving, label = "Sauvegarder" }) {
  return (
    <button onClick={onClick} disabled={saving}
      className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:scale-105 disabled:opacity-50 mt-2"
      style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)' }}>
      <Save size={14} /> {saving ? 'Sauvegarde...' : label}
    </button>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [content, setContent] = useState({});
  const [services, setServices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('all');
  const [zones, setZones] = useState([]);
  const [editZone, setEditZone] = useState(null);
  const [cutoff, setCutoff] = useState({ enabled: false, day_of_week: 1, hour: 12, minute: 0, message: 'Les commandes sont fermees pour cette semaine.' });

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(''), 3000); };

  const load = useCallback(async () => {
    try {
      const [c, s, j, ct, ap, ord, zn] = await Promise.all([
        axios.get(`${API}/api/content`),
        axios.get(`${API}/api/services`),
        axios.get(`${API}/api/jobs`),
        axios.get(`${API}/api/contact`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/api/career`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/api/orders`, auth()).catch(() => ({ data: [] })),
        axios.get(`${API}/api/delivery-zones`).catch(() => ({ data: [] })),
      ]);
      setContent(c.data);
      setServices(s.data);
      setJobs(j.data);
      setContacts(ct.data);
      setApplications(ap.data);
      setOrders(ord.data);
      setZones(zn.data);
      axios.get(`${API}/api/settings/cutoff`).then(r => setCutoff(r.data)).catch(() => {});
    } catch { navigate('/admin'); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const updateField = (section, key, val) => {
    setContent(prev => ({ ...prev, [section]: { ...prev[section], [key]: val } }));
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/content/${section}`, { section, data: content[section] }, auth());
      flash(`${section} sauvegarde !`);
    } catch { flash('Erreur'); }
    setSaving(false);
  };

  // Services
  const updateService = (i, key, val) => {
    setServices(prev => { const n = [...prev]; n[i] = { ...n[i], [key]: val }; return n; });
  };
  const saveService = async (svc) => {
    setSaving(true);
    try {
      if (svc.id) { await axios.put(`${API}/api/services/${svc.id}`, svc, auth()); }
      else { await axios.post(`${API}/api/services`, svc, auth()); }
      flash('Service sauvegarde !');
      load();
    } catch { flash('Erreur'); }
    setSaving(false);
  };
  const deleteService = async (id) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    await axios.delete(`${API}/api/services/${id}`, auth());
    load();
  };

  // Jobs
  const updateJob = (i, key, val) => {
    setJobs(prev => { const n = [...prev]; n[i] = { ...n[i], [key]: val }; return n; });
  };
  const saveJob = async (job) => {
    setSaving(true);
    try {
      if (job.id) { await axios.put(`${API}/api/jobs/${job.id}`, job, auth()); }
      else { await axios.post(`${API}/api/jobs`, job, auth()); }
      flash('Emploi sauvegarde !');
      load();
    } catch { flash('Erreur'); }
    setSaving(false);
  };
  const deleteJob = async (id) => {
    if (!window.confirm('Supprimer cet emploi ?')) return;
    await axios.delete(`${API}/api/jobs/${id}`, auth());
    load();
  };

  const logout = () => { localStorage.removeItem('admin_token'); navigate('/admin'); };

  const hero = content.hero || {};
  const ci = content.contact_info || {};
  const links = content.links || {};
  const about = content.about || {};

  return (
    <div className="min-h-screen" style={{ background: '#121611' }} data-testid="admin-dashboard">
      <div className="sticky top-0 z-50 flex items-center justify-between px-6 py-3 border-b" style={{ backgroundColor: 'rgba(18,22,17,0.95)', borderColor: '#2C3529', backdropFilter: 'blur(10px)' }}>
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Admin — Concept Gourmet</h1>
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition-colors">
            <Eye size={12} /> Voir le site
          </a>
        </div>
        <div className="flex items-center gap-3">
          {msg && <span className="text-sm font-medium text-[#6DAE65] animate-pulse">{msg}</span>}
          <button onClick={logout} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors" data-testid="admin-logout-btn">
            <LogOut size={14} /> Deconnexion
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Orders */}
        <Section title="Commandes" icon={ShoppingBag} count={orders.length} defaultOpen={true}>
          <div className="flex gap-2 mb-4 flex-wrap">
            {[['all', 'Toutes'], ['new', 'Nouvelles'], ['preparing', 'En preparation'], ['ready', 'Pretes'], ['delivered', 'Livrees'], ['cancelled', 'Annulees']].map(([val, label]) => (
              <button key={val} onClick={() => setOrderFilter(val)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${orderFilter === val ? 'text-white' : 'text-white/40 hover:text-white/70'}`}
                style={{ backgroundColor: orderFilter === val ? (val === 'new' ? '#F78A7D' : val === 'preparing' ? '#EAB308' : val === 'ready' ? '#6DAE65' : val === 'delivered' ? '#3B82F6' : val === 'cancelled' ? '#EF4444' : 'rgba(255,255,255,0.15)') : 'rgba(255,255,255,0.05)' }}>
                {label}
              </button>
            ))}
          </div>
          {orders.filter(o => orderFilter === 'all' || o.status === orderFilter).length === 0
            ? <p className="text-sm text-white/30">Aucune commande</p>
            : orders.filter(o => orderFilter === 'all' || o.status === orderFilter).map(order => (
              <div key={order.id} className="p-4 rounded-xl mb-3 space-y-2" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-bold text-white">#{order.id?.slice(0, 8)}</span>
                    <span className="text-xs text-white/30 ml-2">{order.created_at ? new Date(order.created_at).toLocaleString('fr-CA') : ''}</span>
                  </div>
                  <select value={order.status || 'new'}
                    onChange={async (e) => {
                      await axios.put(`${API}/api/orders/${order.id}/status`, { status: e.target.value }, auth());
                      load();
                    }}
                    className="px-3 py-1 rounded-lg text-xs font-semibold outline-none cursor-pointer"
                    style={{
                      backgroundColor: order.status === 'new' ? 'rgba(247,138,125,0.15)' : order.status === 'preparing' ? 'rgba(234,179,8,0.15)' : order.status === 'ready' ? 'rgba(109,174,101,0.15)' : order.status === 'delivered' ? 'rgba(59,130,246,0.15)' : 'rgba(239,68,68,0.15)',
                      color: order.status === 'new' ? '#F78A7D' : order.status === 'preparing' ? '#EAB308' : order.status === 'ready' ? '#6DAE65' : order.status === 'delivered' ? '#3B82F6' : '#EF4444',
                      border: '1px solid #2C3529',
                    }}>
                    <option value="new">Nouvelle</option>
                    <option value="preparing">En preparation</option>
                    <option value="ready">Prete</option>
                    <option value="delivered">Livree</option>
                    <option value="cancelled">Annulee</option>
                  </select>
                </div>
                <div className="flex items-center gap-3 text-xs text-white/50">
                  <span>{order.form?.name || 'Client inconnu'}</span>
                  <span>{order.form?.email}</span>
                  <span>{order.form?.phone}</span>
                  <span className="flex items-center gap-1">{order.delivery === 'delivery' ? <><Truck size={12} /> Livraison</> : <><Store size={12} /> Cueillette</>}</span>
                  {order.form?.date && <span className="flex items-center gap-1"><Clock size={12} /> {order.form.date}</span>}
                </div>
                {order.delivery_zone && <p className="text-xs text-white/40">Zone: {order.delivery_zone.name} — Frais: {order.delivery_zone.fee?.toFixed(2)} $</p>}
                <div className="space-y-1">
                  {(order.items || []).map((item, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-white/70">{item.name} ({item.format}) x{item.qty}</span>
                      <span className="text-[#F78A7D] font-medium">{(item.price * item.qty).toFixed(2)} $</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between pt-2 border-t" style={{ borderColor: '#2C3529' }}>
                  <span className="text-sm font-bold text-white">Total</span>
                  <span className="text-sm font-bold text-[#F78A7D]">{order.total?.toFixed(2)} $</span>
                </div>
              </div>
            ))}
        </Section>

        {/* Delivery Zones */}
        <Section title="Zones livraison" icon={Truck} count={zones.length} defaultOpen={false}>
          <p className="text-xs text-white/40 mb-3">Configurez les zones de livraison. Les clients verront ces zones lors du checkout.</p>
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #2C3529' }}>
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-white/30" style={{ backgroundColor: '#0E120D' }}>
              <span className="col-span-5">Nom</span>
              <span className="col-span-2 text-center">Frais</span>
              <span className="col-span-3 text-center">Min. Commande</span>
              <span className="col-span-2 text-right">Actions</span>
            </div>
            {zones.map(zone => (
              <div key={zone.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-t" style={{ borderColor: '#2C3529' }}>
                {editZone?.id === zone.id ? (
                  <>
                    <input className="col-span-5 px-2 py-1 rounded text-sm text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }}
                      value={editZone.name} onChange={e => setEditZone(z => ({ ...z, name: e.target.value }))} />
                    <input type="number" step="0.01" className="col-span-2 px-2 py-1 rounded text-sm text-center text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }}
                      value={editZone.fee} onChange={e => setEditZone(z => ({ ...z, fee: parseFloat(e.target.value) || 0 }))} />
                    <input type="number" step="0.01" className="col-span-3 px-2 py-1 rounded text-sm text-center text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }}
                      value={editZone.min_order} onChange={e => setEditZone(z => ({ ...z, min_order: parseFloat(e.target.value) || 0 }))} />
                    <div className="col-span-2 flex justify-end gap-1">
                      <button onClick={async () => { await axios.put(`${API}/api/delivery-zones/${editZone.id}`, editZone, auth()); setEditZone(null); load(); }}
                        className="p-1.5 rounded text-[#6DAE65] hover:bg-[#6DAE65]/10"><Check size={14} /></button>
                      <button onClick={() => setEditZone(null)} className="p-1.5 rounded text-white/30 hover:bg-white/10"><XIcon size={14} /></button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="col-span-5 text-sm text-white">{zone.name}</span>
                    <span className="col-span-2 text-sm text-center font-semibold" style={{ color: '#EAB308' }}>{zone.fee?.toFixed(2)} $</span>
                    <span className="col-span-3 text-sm text-center text-white/60">{zone.min_order?.toFixed(2)} $</span>
                    <div className="col-span-2 flex justify-end gap-1">
                      <button onClick={() => setEditZone({ ...zone })} className="p-1.5 rounded text-white/30 hover:text-white hover:bg-white/10"><GripVertical size={14} /></button>
                      <button onClick={async () => { if (window.confirm('Supprimer cette zone ?')) { await axios.delete(`${API}/api/delivery-zones/${zone.id}`, auth()); load(); }}}
                        className="p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-red-400/10"><Trash2 size={14} /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <button onClick={async () => {
            await axios.post(`${API}/api/delivery-zones`, { name: 'Nouvelle zone', fee: 0, min_order: 0 }, auth());
            load();
          }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-[#F78A7D] hover:bg-[#F78A7D]/10 transition-colors w-full justify-center mt-3"
            style={{ border: '1px dashed #F78A7D33' }}>
            <Plus size={16} /> Nouvelle zone
          </button>
        </Section>

        {/* Cutoff */}
        <Section title="Cutoff commandes" icon={Clock} defaultOpen={false}>
          <div className="space-y-6">
            {/* Window */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-bold text-white">Fenetre de commande</h3>
                <button onClick={() => setCutoff(c => ({ ...c, window_enabled: !c.window_enabled }))}
                  className={`w-10 h-5 rounded-full transition-all relative ${cutoff.window_enabled ? 'bg-[#6DAE65]' : 'bg-white/10'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${cutoff.window_enabled ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>
              {cutoff.window_enabled && (
                <div className="p-4 rounded-xl space-y-3" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
                  <p className="text-xs text-white/40 mb-2">Les commandes sont acceptees uniquement entre l'ouverture et la fermeture.</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6DAE65] mb-1 block">Ouverture — Jour</label>
                      <select value={cutoff.window_open_day} onChange={e => setCutoff(c => ({ ...c, window_open_day: parseInt(e.target.value) }))}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }}>
                        {['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'].map((d,i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6DAE65] mb-1 block">Heure</label>
                      <input type="number" min="0" max="23" value={cutoff.window_open_hour} onChange={e => { let v=parseInt(e.target.value); if(v>23)v=0; if(v<0)v=23; setCutoff(c=>({...c,window_open_hour:v||0})); }}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }} />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6DAE65] mb-1 block">Minute</label>
                      <input type="number" min="0" max="59" value={String(cutoff.window_open_minute||0).padStart(2,'0')} onChange={e => { let v=parseInt(e.target.value); if(v>59)v=0; if(v<0)v=59; setCutoff(c=>({...c,window_open_minute:v||0})); }}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-[#F78A7D] mb-1 block">Fermeture — Jour</label>
                      <select value={cutoff.window_close_day} onChange={e => setCutoff(c => ({ ...c, window_close_day: parseInt(e.target.value) }))}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }}>
                        {['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'].map((d,i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-[#F78A7D] mb-1 block">Heure</label>
                      <input type="number" min="0" max="23" value={cutoff.window_close_hour} onChange={e => { let v=parseInt(e.target.value); if(v>23)v=0; if(v<0)v=23; setCutoff(c=>({...c,window_close_hour:v||0})); }}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }} />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-[#F78A7D] mb-1 block">Minute</label>
                      <input type="number" min="0" max="59" value={String(cutoff.window_close_minute||0).padStart(2,'0')} onChange={e => { let v=parseInt(e.target.value); if(v>59)v=0; if(v<0)v=59; setCutoff(c=>({...c,window_close_minute:v||0})); }}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-semibold text-white/30 mb-1 block">Message (commandes fermees)</label>
                    <input type="text" value={cutoff.window_message||''} onChange={e => setCutoff(c => ({ ...c, window_message: e.target.value }))}
                      className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Cancellation */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-bold text-white">Cutoff annulation</h3>
                <button onClick={() => setCutoff(c => ({ ...c, cancel_enabled: !c.cancel_enabled }))}
                  className={`w-10 h-5 rounded-full transition-all relative ${cutoff.cancel_enabled ? 'bg-[#F78A7D]' : 'bg-white/10'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${cutoff.cancel_enabled ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>
              {cutoff.cancel_enabled && (
                <div className="p-4 rounded-xl space-y-3" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
                  <p className="text-xs text-white/40 mb-2">Delai avant la date de livraison apres lequel le client ne peut plus annuler/modifier.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-white/30 mb-1 block">Mode</label>
                      <select value={cutoff.cancel_mode||'hours'} onChange={e => setCutoff(c => ({ ...c, cancel_mode: e.target.value }))}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }}>
                        <option value="hours">Heures</option>
                        <option value="days">Jours</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-white/30 mb-1 block">Valeur</label>
                      <input type="number" min="1" value={cutoff.cancel_value||24} onChange={e => setCutoff(c => ({ ...c, cancel_value: parseInt(e.target.value)||1 }))}
                        className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-semibold text-white/30 mb-1 block">Message (annulation fermee)</label>
                    <input type="text" value={cutoff.cancel_message||''} onChange={e => setCutoff(c => ({ ...c, cancel_message: e.target.value }))}
                      className="w-full px-2 py-2 rounded-lg text-xs text-white outline-none" style={{ backgroundColor: '#1E241C', border: '1px solid #2C3529' }} />
                  </div>
                </div>
              )}
            </div>

            <button onClick={async () => { await axios.put(`${API}/api/settings/cutoff`, cutoff, auth()); alert('Cutoff sauvegarde !'); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: '#3B82F6' }}>
              <Save size={14} /> Sauvegarder
            </button>
          </div>
        </Section>

        {/* Hero */}
        <Section title="Page d'accueil — Hero" icon={FileText}>
          <Field label="Titre principal" value={hero.title} onChange={v => updateField('hero', 'title', v)} multiline />
          <Field label="Sous-titre / Description" value={hero.subtitle} onChange={v => updateField('hero', 'subtitle', v)} multiline />
          <SaveBtn onClick={() => saveSection('hero')} saving={saving} />
        </Section>

        {/* Services */}
        <Section title="Services" icon={Star} count={services.length} defaultOpen={true}>
          <p className="text-xs text-white/40 mb-2">Modifiez le titre, la description, l'image et le lien de chaque service. L'ordre d'affichage suit l'ordre ci-dessous.</p>
          {services.map((svc, i) => (
            <div key={svc.id || i} className="p-4 rounded-xl space-y-3 mb-3" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-white"><GripVertical size={14} className="text-white/20" /> {svc.title || 'Nouveau service'}</span>
                <div className="flex gap-2">
                  {svc.id && <button onClick={() => deleteService(svc.id)} className="text-red-400/60 hover:text-red-400 text-xs flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>}
                </div>
              </div>
              <Field label="Titre" value={svc.title} onChange={v => updateService(i, 'title', v)} />
              <Field label="Description" value={svc.description} onChange={v => updateService(i, 'description', v)} multiline />
              <ImageUpload currentUrl={svc.image} onUpload={v => updateService(i, 'image', v)} label="Image du service" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Texte du bouton" value={svc.link_text} onChange={v => updateService(i, 'link_text', v)} placeholder="ex: Cliquez ici pour commander" />
                <Field label="URL du bouton" value={svc.link_url} onChange={v => updateService(i, 'link_url', v)} placeholder="https://..." />
              </div>
              <SaveBtn onClick={() => saveService(services[i])} saving={saving} label="Sauvegarder ce service" />
            </div>
          ))}
          <button onClick={() => setServices(prev => [...prev, { title: '', description: '', image: '', link_text: '', link_url: '', order: prev.length }])}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 transition-colors w-full justify-center"
            style={{ border: '1px dashed #2C3529' }}>
            <Plus size={16} /> Ajouter un service
          </button>
        </Section>

        {/* A propos */}
        <Section title="Page A propos" icon={Users}>
          <Field label="Texte biographique" value={about.text} onChange={v => updateField('about', 'text', v)} multiline />
          <Field label="Signature" value={about.signature} onChange={v => updateField('about', 'signature', v)} placeholder="-Caroline Belanger-" />
          <Field label="Titre" value={about.title} onChange={v => updateField('about', 'title', v)} placeholder="Chef | Proprietaire" />
          <ImageUpload currentUrl={about.image} onUpload={v => updateField('about', 'image', v)} label="Photo" />
          <SaveBtn onClick={() => saveSection('about')} saving={saving} />
        </Section>

        {/* Contact */}
        <Section title="Coordonnees" icon={Phone}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Telephone" value={ci.phone} onChange={v => updateField('contact_info', 'phone', v)} />
            <Field label="Courriel" value={ci.email} onChange={v => updateField('contact_info', 'email', v)} />
            <Field label="Adresse" value={ci.address} onChange={v => updateField('contact_info', 'address', v)} />
            <Field label="Ville" value={ci.city} onChange={v => updateField('contact_info', 'city', v)} />
            <Field label="Code postal" value={ci.postal} onChange={v => updateField('contact_info', 'postal', v)} />
          </div>
          <Field label="Heures semaine" value={ci.hours_weekday} onChange={v => updateField('contact_info', 'hours_weekday', v)} />
          <Field label="Heures weekend" value={ci.hours_weekend} onChange={v => updateField('contact_info', 'hours_weekend', v)} />
          <SaveBtn onClick={() => saveSection('contact_info')} saving={saving} />
        </Section>

        {/* Links */}
        <Section title="Liens de commande" icon={Link2}>
          <Field label="Commande en ligne" value={links.commande_en_ligne} onChange={v => updateField('links', 'commande_en_ligne', v)} />
          <Field label="Commande Scolaire" value={links.commande_scolaire} onChange={v => updateField('links', 'commande_scolaire', v)} />
          <Field label="Commande Garderie/CPE" value={links.commande_garderie} onChange={v => updateField('links', 'commande_garderie', v)} />
          <Field label="Facebook" value={links.facebook} onChange={v => updateField('links', 'facebook', v)} />
          <SaveBtn onClick={() => saveSection('links')} saving={saving} />
        </Section>

        {/* Jobs */}
        <Section title="Offres d'emploi" icon={Briefcase} count={jobs.length}>
          {jobs.map((job, i) => (
            <div key={job.id || i} className="p-4 rounded-xl space-y-3 mb-3" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white">{job.title || 'Nouveau poste'}</span>
                {job.id && <button onClick={() => deleteJob(job.id)} className="text-red-400/60 hover:text-red-400 text-xs flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Titre" value={job.title} onChange={v => updateJob(i, 'title', v)} />
                <Field label="Lieu" value={job.location} onChange={v => updateJob(i, 'location', v)} />
                <Field label="Type" value={job.type} onChange={v => updateJob(i, 'type', v)} />
                <Field label="Note" value={job.note} onChange={v => updateJob(i, 'note', v)} />
              </div>
              <Field label="Taches (une par ligne)" value={(job.tasks || []).join('\n')} multiline onChange={v => updateJob(i, 'tasks', v.split('\n').filter(Boolean))} />
              <SaveBtn onClick={() => saveJob(jobs[i])} saving={saving} label="Sauvegarder" />
            </div>
          ))}
          <button onClick={() => setJobs(prev => [...prev, { title: '', location: '', type: '', tasks: [], note: '' }])}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 transition-colors w-full justify-center"
            style={{ border: '1px dashed #2C3529' }}>
            <Plus size={16} /> Ajouter un emploi
          </button>
        </Section>

        {/* Messages */}
        <Section title="Messages recus" icon={MessageSquare} count={contacts.length}>
          {contacts.length === 0 ? <p className="text-sm text-white/30">Aucun message</p> : contacts.slice(0, 20).map((c, i) => (
            <div key={i} className="p-3 rounded-lg text-sm space-y-1" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
              <div className="flex justify-between"><span className="font-semibold text-white">{c.name}</span><span className="text-xs text-white/30">{c.timestamp ? new Date(c.timestamp).toLocaleDateString('fr-CA') : ''}</span></div>
              <p className="text-white/50 text-xs">{c.email} {c.phone && ` | ${c.phone}`} | {c.city}</p>
              <p className="text-white/70">{c.message}</p>
            </div>
          ))}
        </Section>

        {/* Candidatures */}
        <Section title="Candidatures" icon={Users} count={applications.length}>
          {applications.length === 0 ? <p className="text-sm text-white/30">Aucune candidature</p> : applications.slice(0, 20).map((a, i) => (
            <div key={i} className="p-3 rounded-lg text-sm space-y-1" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
              <div className="flex justify-between"><span className="font-semibold text-white">{a.firstName} {a.lastName}</span><span className="text-xs text-white/30">{a.timestamp ? new Date(a.timestamp).toLocaleDateString('fr-CA') : ''}</span></div>
              <p className="text-white/50 text-xs">{a.email} {a.phone && ` | ${a.phone}`} {a.position && ` | ${a.position}`}</p>
              {a.message && <p className="text-white/70">{a.message}</p>}
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}
